import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import { useLiff } from '../../contexts/LiffContext';
import { logErrorToBackend } from '../../utils/errorLogger';
import {
  getGroupIdFromUrl,
  getLiteReturnPath,
  isSquarePath,
} from '../../utils/getGroupIdFromUrl';
import { buildLiteJoinUrl } from '../../utils/lite-join-url';
import { GroupStorage } from '../../utils/groupStorage';
import { getLiteJwtToken, setLiteJwtToken } from '../../api/liteAuthToken';
import {
  createActivity,
  deleteActivity,
  duplicateActivity,
  getActivities,
  recognizeMenuImages,
  type UpdateActivityRequest,
} from '../../api/liteActivitiesClient';
import { swrListConfig } from '../../lib/swr-config';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { MobileCard, MobileCardTitle } from '@/components/ui/mobile-card';
import { MobileList } from '@/components/ui/mobile-list';
import { MobileListItem } from '@/components/ui/mobile-list-item';
import { MobileInput, MobileTextarea } from '@/components/ui/mobile-input';
import { CreateActivityEntryView } from '@/components/CreateActivityEntryView';
import { CreateActivityFormLayout } from '@/components/CreateActivityFormLayout';
import { checkGroupTenant } from '../../api/contextClient';
import { getSquareBySuffix } from '../../api/liteSquaresClient';
import { isLitePrepaidShopType } from '@pos/shared';
// lite_products API 已移除，商品信息現在存儲在 lite_activities.productList (jsonb) 中

/** 單一規格（用於多規格項目） */
interface PurchaseListSpec {
  id: string;
  name: string;
  price: number;
}

/** 單一商品項目 */
interface PurchaseListItemSimple {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

/** 多規格項目（共用總量上限） */
interface PurchaseListItemVariant {
  id: string;
  name: string;
  maxTotalQuantity: number;
  specs: PurchaseListSpec[];
  /** 發佈時帶入預繳名單（預設 true） */
  includePrepaidOnPublish?: boolean;
}

/** 購買清單項目：單一商品或多規格 */
type PurchaseListItem = PurchaseListItemSimple | PurchaseListItemVariant;

/** 是否為多規格項目 */
function isPurchaseListItemVariant(
  item: PurchaseListItem
): item is PurchaseListItemVariant {
  return (
    Array.isArray((item as PurchaseListItemVariant).specs) &&
    (item as PurchaseListItemVariant).specs.length > 0
  );
}

/**
 * 購買清單
 */
interface PurchaseList {
  id: string;
  title: string;
  description?: string;
  items: PurchaseListItem[];
  createdAt: string;
  createdBy: string;
  startDate?: string; // 開始日期 (ISO 8601)
  endDate?: string; // 結束日期 (ISO 8601)
  status?: 'draft' | 'active' | 'inactive'; // 活動狀態：draft=未發佈, active=進行中, inactive=已結束
}

/**
 * 建立一個空白商品項目（用於初始化清單）
 */
function createEmptyPurchaseListItem(): PurchaseListItemSimple {
  return {
    id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: '',
    price: 0,
    quantity: 0,
  };
}

/** 建立空白規格 */
function createEmptySpec(): PurchaseListSpec {
  return {
    id: `spec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: '',
    price: 0,
  };
}

/**
 * 建立購買清單頁面
 * 允許使用者建立購買清單或活動，並產生分享連結
 */
/**
 * 取得 JWT Token：優先處理 URL 帶入，其次統一從 liteAuthToken 取得
 */
function getJwtToken(): string | null {
  if (typeof window === 'undefined') return null;

  // 從 URL 參數取得（例如 OAuth/導回或 debug 情境）
  const urlParams = new URLSearchParams(window.location.search);
  const fromUrl = urlParams.get('jwt_token') || urlParams.get('token');
  if (fromUrl) {
    setLiteJwtToken(fromUrl);
    return fromUrl;
  }

  return getLiteJwtToken();
}

/** 從 pathname 取出 Square suffix（/lite/shop/XXXXXX/...），避免 useParams 未提供時仍能帶正確 header */
function getSquareSuffixFromPathname(pathname: string): string | null {
  const m = pathname.match(/^\/lite\/shop\/([A-Za-z0-9]{6,8})(?:\/|$)/);
  return m ? m[1] : null;
}

export function HoldPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { suffix: suffixFromParams } = useParams<{ suffix?: string }>();
  const { profile, liff } = useLiff();
  const groupId = getGroupIdFromUrl();
  /** Square 模式：pathname 為 /lite/shop/:suffix 時一律視為 Square；suffix 優先 useParams，否則從 pathname 解析 */
  const isSquare = isSquarePath(location.pathname);
  const suffix =
    suffixFromParams &&
    (suffixFromParams.length === 6 || suffixFromParams.length === 8)
      ? suffixFromParams
      : isSquare
        ? getSquareSuffixFromPathname(location.pathname)
        : null;
  const isSquareMode = Boolean(isSquare && suffix);

  // 追蹤組件掛載和卸載
  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`[HoldPage] [${timestamp}] 🎬 組件掛載`);
    return () => {
      const unmountTimestamp = new Date().toISOString();
      console.log(`[HoldPage] [${unmountTimestamp}] 🛑 組件卸載`);
    };
  }, []);

  const [listTitle, setListTitle] = useState('');
  const [listDescription, setListDescription] = useState('');
  // 一開始清單就要有一個項目（避免空白畫面）
  const [items, setItems] = useState<PurchaseListItem[]>(() => [
    createEmptyPurchaseListItem(),
  ]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [autoPublish, setAutoPublish] = useState(false);
  const [autoSettle, setAutoSettle] = useState(false);
  const [savedListId, setSavedListId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null); // 儲存錯誤訊息
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dragOverItemId, setDragOverItemId] = useState<string | null>(null);
  const [savedLists, setSavedLists] = useState<PurchaseList[]>([]);
  const [editingActivityId, setEditingActivityId] = useState<string | null>(
    null
  ); // 正在編輯的活動 ID
  /** 當前商家類型（僅席次(羽球)顯示預繳專用、發佈時帶入預繳等） */
  const [currentShopType, setCurrentShopType] = useState<string | null>(null);

  // 追蹤 savedLists 的每次變化
  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`[HoldPage] [${timestamp}] 📊 savedLists 狀態變化:`, {
      length: savedLists.length,
      lists: savedLists.map(list => ({
        id: list.id,
        title: list.title,
        status: list.status,
      })),
    });
  }, [savedLists]);

  /** 是否為一般「商品」類型商家（非席次/預繳） */
  const isProductShopType =
    currentShopType != null && currentShopType.trim() === '商品';

  /** 依商家類型切換欄位文案：商品 vs 票券 */
  const itemNameLabel = isProductShopType ? '商品' : '票券';
  const itemNamePlaceholder = isProductShopType ? '商品名稱' : '票券名稱';

  // 載入當前商家類型（僅席次(羽球)顯示預繳相關選項）
  useEffect(() => {
    if (isSquareMode && suffix) {
      getSquareBySuffix(suffix)
        .then(sq => setCurrentShopType(sq.type ?? null))
        .catch(() => setCurrentShopType(null));
      return;
    }
    if (groupId) {
      const jwt = getLiteJwtToken();
      if (!jwt) {
        setCurrentShopType(null);
        return;
      }
      checkGroupTenant(jwt, groupId)
        .then(r => setCurrentShopType(r?.tenant?.type ?? null))
        .catch(() => setCurrentShopType(null));
      return;
    }
    setCurrentShopType(null);
  }, [groupId, isSquareMode, suffix]);
  const [itemErrors, setItemErrors] = useState<
    Record<string, { price?: string; quantity?: string }>
  >({});
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set()); // 追蹤展開的項目
  const [isFormVisible, setIsFormVisible] = useState(false); // 預設顯示入口（新增活動／從菜單建立），點選後才顯示表單
  const [touchDragState, setTouchDragState] = useState<{
    itemId: string | null;
    startY: number;
    currentY: number;
    targetIndex: number | null;
  } | null>(null); // 手機拖曳狀態
  // 移除側滑狀態，改用 ActionSheet
  // const [swipeState, setSwipeState] = useState<{
  //   listId: string | null;
  //   startX: number;
  //   currentX: number;
  // } | null>(null); // 滑動刪除狀態
  // 移除側滑相關狀態，改用 ActionSheet
  // const [swipedItems, setSwipedItems] = useState<Set<string>>(new Set()); // 追蹤已滑動顯示刪除的項目
  const [actionSheetOpened, setActionSheetOpened] = useState(false); // ActionSheet 開關
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(
    null
  ); // 選中的活動 ID
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false); // 刪除確認對話框
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogLink, setShareDialogLink] = useState('');
  const [shareDialogTitle, setShareDialogTitle] = useState('');
  /** 發佈後分享到 LINE 的完整文字（依自訂範本或標題+連結） */
  const [shareDialogText, setShareDialogText] = useState('');

  /** 移除每一行開頭多餘的空白（特別是 URL 前面的縮排），避免預覽與 LINE 內文出現莫名縮排 */
  const normalizeShareText = (raw: string): string => {
    if (!raw) return '';
    return raw
      .split('\n')
      .map(line => line.replace(/^\s+(https?:\/\/)/u, '$1'))
      .join('\n')
      .trimEnd();
  };
  // 從菜單建立：上傳圖片辨識草稿
  const [menuRecognizeOpen, setMenuRecognizeOpen] = useState(false);
  const [menuRecognizeFiles, setMenuRecognizeFiles] = useState<File[]>([]);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [recognizeError, setRecognizeError] = useState<string | null>(null);

  // SWR key 用於活動列表（Group 用 groupId，Square 用 suffix）
  const swrKey =
    groupId || isSquareMode
      ? isSquareMode
        ? ['lite-activities', 'square', suffix]
        : ['lite-activities', groupId]
      : null;

  /** 列表若由 Square API 載入，對列表項的操作一律用同一個 suffix，與列表來源一致，避免「已用 square 篩出卻用 group 操作」 */
  const listSquareSuffix: string | null =
    Array.isArray(swrKey) &&
    swrKey[1] === 'square' &&
    typeof swrKey[2] === 'string'
      ? swrKey[2]
      : null;

  // SWR fetcher 函數
  const fetcher = useCallback(async (): Promise<PurchaseList[]> => {
    const jwtToken = getJwtToken();
    if (!jwtToken) {
      throw new Error('請先登入');
    }

    const result = await getActivities(jwtToken, {
      ...(listSquareSuffix ? { squareSuffix: listSquareSuffix } : {}),
    });

    // 轉換為 PurchaseList 格式
    const activities: PurchaseList[] = result.activities.map(activity => ({
      id: activity.id,
      title: activity.title,
      description: activity.description || undefined,
      items: [], // 商品項目需要另外載入，這裡先留空
      createdAt: activity.createdAt,
      createdBy: activity.createdBy || '',
      startDate: activity.startDate || undefined,
      endDate: activity.endDate || undefined,
      status: (activity.status as 'draft' | 'active' | 'inactive') || 'draft',
    }));

    return activities;
  }, [listSquareSuffix]);

  // 使用 SWR 載入活動列表
  const { data: swrActivities, mutate: mutateActivities } = useSWR<
    PurchaseList[]
  >(swrKey, fetcher, {
    ...swrListConfig,
    revalidateOnFocus: false, // 手動控制重新驗證
  });

  // 同步 SWR 資料到 savedLists state
  useEffect(() => {
    if (swrActivities) {
      setSavedLists(swrActivities);
    }
  }, [swrActivities]);

  // 載入現有清單（Group 用 GroupStorage；Square 模式不讀本地草稿）
  useEffect(() => {
    if (!groupId || isSquareMode) return;

    try {
      const groupStorage = new GroupStorage(groupId);
      const savedList = groupStorage.getItem<PurchaseList>(groupId);
      if (savedList) {
        setListTitle(savedList.title || '');
        setListDescription(savedList.description || '');
        setItems(savedList.items || []);
        setStartDate(savedList.startDate || '');
        setEndDate(savedList.endDate || '');
        // 如果有已儲存的清單，隱藏表單
        setIsFormVisible(false);
      }
    } catch (error) {
      console.error('[HoldPage] 載入清單失敗:', error);
      logErrorToBackend(error, {
        context: {
          action: 'loadList',
          groupId,
        },
      });
    }
  }, [groupId, isSquareMode]);

  /**
   * 新增商品項目（點擊 + 按鈕時）
   * 直接添加一個空項目，並自動展開讓用戶填寫
   */
  const handleAddItem = () => {
    const newItem: PurchaseListItem = {
      id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: '',
      price: 0,
      quantity: 0,
    };

    // 添加新項目，收合所有已存在的項目，但新項目自動展開
    setItems(prev => {
      return [...prev, newItem];
    });

    // 收合所有已存在的項目，但展開新項目
    setExpandedItems(new Set([newItem.id]));
  };

  /**
   * 驗證項目欄位
   */
  const validateItemField = (
    _itemId: string,
    field: 'price' | 'quantity',
    value: string | number
  ): string | null => {
    if (field === 'price') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      if (typeof numValue !== 'number' || isNaN(numValue) || numValue < 0) {
        return '請輸入有效的價格（必須為大於等於 0 的數字）';
      }
    } else if (field === 'quantity') {
      const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
      // 允許 0（用戶正在編輯，可能暫時為空），但在儲存時會檢查
      if (
        typeof numValue !== 'number' ||
        isNaN(numValue) ||
        numValue < 0 ||
        !Number.isInteger(numValue)
      ) {
        return '請輸入有效的數量（必須為大於等於 0 的整數）';
      }
      // 只有在儲存時才檢查是否 >= 1，編輯時允許 0
    }
    return null;
  };

  /**
   * 更新項目（單一商品的 name/price/quantity；多規格僅更新 name）
   */
  const handleUpdateItem = (
    itemId: string,
    field: 'name' | 'price' | 'quantity',
    value: string | number
  ) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId) return item;
        if (isPurchaseListItemVariant(item)) {
          if (field === 'name') return { ...item, name: String(value) };
          return item;
        }
        if (field === 'name') return { ...item, name: String(value) };
        if (field === 'price') {
          const numValue =
            typeof value === 'string' ? parseFloat(value) : value;
          const finalValue = isNaN(numValue) ? 0 : numValue;
          // 驗證並更新錯誤
          const error = validateItemField(itemId, 'price', finalValue);
          if (!error) {
            // 清除錯誤
            setItemErrors(prevErrors => {
              const newErrors = { ...prevErrors };
              if (newErrors[itemId]) {
                delete newErrors[itemId].price;
                if (Object.keys(newErrors[itemId]).length === 0) {
                  delete newErrors[itemId];
                }
              }
              return newErrors;
            });
          } else {
            // 設置錯誤
            setItemErrors(prevErrors => ({
              ...prevErrors,
              [itemId]: {
                ...prevErrors[itemId],
                price: error,
              },
            }));
          }
          return {
            ...item,
            price: finalValue,
          } as PurchaseListItemSimple;
        }
        if (field === 'quantity') {
          const numValue =
            typeof value === 'string' ? parseInt(value, 10) : value;
          const finalValue = isNaN(numValue) ? 0 : numValue;
          // 驗證並更新錯誤
          const error = validateItemField(itemId, 'quantity', finalValue);
          if (!error) {
            // 清除錯誤
            setItemErrors(prevErrors => {
              const newErrors = { ...prevErrors };
              if (newErrors[itemId]) {
                delete newErrors[itemId].quantity;
                if (Object.keys(newErrors[itemId]).length === 0) {
                  delete newErrors[itemId];
                }
              }
              return newErrors;
            });
          } else {
            // 設置錯誤
            setItemErrors(prevErrors => ({
              ...prevErrors,
              [itemId]: {
                ...prevErrors[itemId],
                quantity: error,
              },
            }));
          }
          return {
            ...item,
            quantity: finalValue,
          } as PurchaseListItemSimple;
        }
        return item;
      })
    );
  };

  /**
   * 刪除商品項目
   */
  const handleRemoveItem = (itemId: string) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
    toast.success('已移除項目');
  };

  /** 將單一商品改為多規格（共用總量） */
  const handleConvertToVariant = (itemId: string) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId || isPurchaseListItemVariant(item)) return item;
        const simple = item as PurchaseListItemSimple;
        return {
          id: simple.id,
          name: simple.name,
          maxTotalQuantity: simple.quantity > 0 ? simple.quantity : 40,
          specs: [
            {
              id: `spec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: simple.name,
              price: simple.price,
            },
          ],
        } as PurchaseListItemVariant;
      })
    );
    setExpandedItems(prev => new Set(prev).add(itemId));
  };

  /** 將單一商品改為「男女不同價」多規格（共用總量） */
  const handleConvertToGenderVariant = (itemId: string) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId || isPurchaseListItemVariant(item)) return item;
        const simple = item as PurchaseListItemSimple;
        const baseName = simple.name || '票券';
        const maxTotalQuantity =
          simple.quantity && simple.quantity > 0 ? simple.quantity : 40;
        const basePrice = simple.price || 0;
        return {
          id: simple.id,
          name: baseName,
          maxTotalQuantity,
          specs: [
            {
              id: `spec_male_${Date.now()}_${Math.random()
                .toString(36)
                .substr(2, 9)}`,
              name: '男',
              price: basePrice,
            },
            {
              id: `spec_female_${Date.now()}_${Math.random()
                .toString(36)
                .substr(2, 9)}`,
              name: '女',
              price: basePrice,
            },
          ],
          includePrepaidOnPublish: true,
        } as PurchaseListItemVariant;
      })
    );
    setExpandedItems(prev => new Set(prev).add(itemId));
  };

  /** 多規格項目：新增一筆規格 */
  const handleAddSpec = (itemId: string) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId || !isPurchaseListItemVariant(item)) return item;
        return {
          ...item,
          specs: [...item.specs, createEmptySpec()],
        };
      })
    );
  };

  /** 多規格項目：更新規格欄位 */
  const handleUpdateSpec = (
    itemId: string,
    specId: string,
    field: keyof PurchaseListSpec,
    value: string | number
  ) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId || !isPurchaseListItemVariant(item)) return item;
        const numValue =
          field === 'price'
            ? typeof value === 'string'
              ? parseFloat(value)
              : value
            : value;
        return {
          ...item,
          specs: item.specs.map(s =>
            s.id === specId
              ? {
                  ...s,
                  [field]:
                    field === 'price'
                      ? isNaN(numValue as number)
                        ? 0
                        : numValue
                      : value,
                }
              : s
          ),
        };
      })
    );
  };

  /** 多規格項目：刪除規格 */
  const handleRemoveSpec = (itemId: string, specId: string) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId || !isPurchaseListItemVariant(item)) return item;
        const next = item.specs.filter(s => s.id !== specId);
        if (next.length === 0) {
          // 若無規格則改回單一商品
          return {
            id: item.id,
            name: item.name,
            price: 0,
            quantity: item.maxTotalQuantity,
          } as PurchaseListItemSimple;
        }
        return { ...item, specs: next };
      })
    );
  };

  /** 多規格項目：更新總量上限 */
  const handleUpdateVariantMaxTotal = (itemId: string, value: number) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId || !isPurchaseListItemVariant(item)) return item;
        return { ...item, maxTotalQuantity: value };
      })
    );
  };

  /** 多規格項目：發佈時是否帶入預繳名單 */
  const handleSetIncludePrepaidOnPublish = (
    itemId: string,
    checked: boolean
  ) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== itemId || !isPurchaseListItemVariant(item)) return item;
        return { ...item, includePrepaidOnPublish: checked };
      })
    );
  };

  /**
   * 處理輸入框獲得焦點時的鍵盤彈起（確保輸入框可見）
   */
  const handleInputFocus = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // 鍵盤彈起時，確保輸入框可見
    // 延遲執行以等待鍵盤完全彈起（Android 和 iOS 的鍵盤動畫時間不同）
    setTimeout(() => {
      // 檢查 scrollIntoView 是否存在（測試環境中可能不存在）
      if (
        e.target &&
        typeof (e.target as HTMLElement).scrollIntoView === 'function'
      ) {
        (e.target as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }, 300);
  };

  /**
   * 拖曳排序處理（桌面版 - HTML5 Drag and Drop）
   */
  const handleDragStart = (itemId: string) => {
    setDraggedItemId(itemId);
  };

  const handleDragOver = (e: React.DragEvent, itemId: string) => {
    e.preventDefault();
    if (draggedItemId && draggedItemId !== itemId) {
      setDragOverItemId(itemId);
    }
  };

  const handleDragLeave = () => {
    setDragOverItemId(null);
  };

  const handleDrop = (e: React.DragEvent, targetItemId: string) => {
    e.preventDefault();
    if (!draggedItemId || draggedItemId === targetItemId) {
      setDraggedItemId(null);
      setDragOverItemId(null);
      return;
    }

    const draggedIndex = items.findIndex(item => item.id === draggedItemId);
    const targetIndex = items.findIndex(item => item.id === targetItemId);

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedItemId(null);
      setDragOverItemId(null);
      return;
    }

    const newItems = [...items];
    const [removed] = newItems.splice(draggedIndex, 1);
    newItems.splice(targetIndex, 0, removed);

    setItems(newItems);
    setDraggedItemId(null);
    setDragOverItemId(null);
  };

  const handleDragEnd = () => {
    setDraggedItemId(null);
    setDragOverItemId(null);
  };

  /**
   * 手機版拖曳排序處理（Touch Events）
   */
  const handleTouchStart = (e: React.TouchEvent, itemId: string) => {
    e.stopPropagation();
    const touch = e.touches[0];
    const itemIndex = items.findIndex(item => item.id === itemId);

    setTouchDragState({
      itemId,
      startY: touch.clientY,
      currentY: touch.clientY,
      targetIndex: itemIndex,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchDragState || touchDragState.targetIndex === null) return;

    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    const currentY = touch.clientY;

    // 計算移動距離，找出目標位置
    const itemElements = document.querySelectorAll('[data-item-id]');
    let targetIndex: number | null = touchDragState.targetIndex;

    itemElements.forEach((el, index) => {
      const rect = el.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;

      if (currentY < centerY && index < touchDragState.targetIndex!) {
        targetIndex = index;
      } else if (currentY > centerY && index > touchDragState.targetIndex!) {
        targetIndex = index;
      }
    });

    setTouchDragState({
      ...touchDragState,
      currentY,
      targetIndex:
        targetIndex !== null ? targetIndex : touchDragState.targetIndex,
    });
  };

  const handleTouchEnd = () => {
    if (!touchDragState) return;

    const { itemId, targetIndex } = touchDragState;
    const draggedIndex = items.findIndex(item => item.id === itemId);

    if (
      draggedIndex !== -1 &&
      targetIndex !== null &&
      draggedIndex !== targetIndex
    ) {
      const newItems = [...items];
      const [removed] = newItems.splice(draggedIndex, 1);
      newItems.splice(targetIndex, 0, removed);
      setItems(newItems);
      toast.success('項目順序已更新');
    }

    setTouchDragState(null);
  };

  /**
   * 儲存清單並產生分享連結
   */
  const handleSaveList = async () => {
    // 清除之前的錯誤
    setSaveError(null);

    console.log('[HoldPage] 🔥🔥🔥 儲存按鈕被點擊了！🔥🔥🔥', {
      timestamp: new Date().toISOString(),
      isSaving,
      listTitle: listTitle.trim(),
      listDescription: listDescription.trim(),
      itemsLength: items.length,
      currentUrl: window.location.href,
      searchParams: window.location.search,
      items: items.map((item, index) => ({
        index: index + 1,
        id: item.id,
        name: item.name,
        nameLength: item.name?.length || 0,
        ...(isPurchaseListItemVariant(item)
          ? {
              maxTotalQuantity: item.maxTotalQuantity,
              specsCount: item.specs.length,
            }
          : {
              price: (item as PurchaseListItemSimple).price,
              priceType: typeof (item as PurchaseListItemSimple).price,
              quantity: (item as PurchaseListItemSimple).quantity,
              quantityType: typeof (item as PurchaseListItemSimple).quantity,
            }),
      })),
    });

    // 驗證活動名稱
    if (!listTitle.trim()) {
      const errorMsg = '請輸入活動名稱';
      console.error('[HoldPage] 儲存失敗: 活動名稱為空', {
        errorMsg,
        listTitle,
        itemsLength: items.length,
      });
      setSaveError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // 驗證項目數量（過濾掉沒有名稱的項目後）
    const itemsWithName = items.filter(item => item.name && item.name.trim());
    if (itemsWithName.length === 0) {
      const errorMsg = '請至少新增一個有效的項目（需包含名稱）';
      console.error('[HoldPage] 儲存失敗: 沒有有效項目', {
        errorMsg,
        listTitle: listTitle.trim(),
        itemsLength: items.length,
        itemsWithNameLength: itemsWithName.length,
      });
      setSaveError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Square 模式須有 suffix；Group 模式須有 groupId
    if (isSquareMode && !suffix) {
      setSaveError('缺少場域資訊');
      toast.error('缺少場域資訊，無法儲存');
      return;
    }
    if (!isSquareMode && !groupId) {
      setSaveError('無法取得群組 ID，請確認 URL 中包含 groupId 參數');
      toast.error('缺少群組 ID，無法儲存');
      return;
    }

    // 防止重複提交（提前到 Square/Group 分支前）
    if (isSaving) {
      console.warn('[HoldPage] 正在儲存中，忽略重複提交');
      return;
    }

    // Square 模式：只呼叫 API，不寫 GroupStorage（與 Group 共用同一套表單與草稿邏輯）
    if (isSquareMode && suffix) {
      const jwtToken = getJwtToken();
      if (!jwtToken) {
        toast.error('請先登入');
        return;
      }
      setIsSaving(true);
      setSaveError(null);
      const productList = itemsWithName.map((item, index) => {
        const id = item.id || `product_${Date.now()}_${index}`;
        if (isPurchaseListItemVariant(item)) {
          return {
            id,
            name: item.name,
            maxTotalQuantity: item.maxTotalQuantity,
            specs: item.specs.map(s => ({
              id: s.id,
              name: s.name,
              price: Math.round(s.price),
            })),
          };
        }
        return {
          id,
          name: item.name,
          price: Math.round(item.price),
          quantity: item.quantity,
        };
      });
      try {
        if (editingActivityId) {
          const { updateActivity } = await import(
            '../../api/liteActivitiesClient'
          );
          const existingList = savedLists.find(
            list => list.id === editingActivityId
          );
          const updateData: UpdateActivityRequest = {
            title: listTitle.trim(),
            description: listDescription.trim() || null,
            startDate: startDate || null,
            endDate: endDate || null,
            productList,
            autoPublish,
            autoSettle,
          };
          if (
            existingList?.status &&
            ['draft', 'active', 'inactive'].includes(existingList.status)
          ) {
            updateData.status = existingList.status as
              | 'draft'
              | 'active'
              | 'inactive';
          }
          const activity = await updateActivity(
            editingActivityId,
            updateData,
            jwtToken,
            listSquareSuffix ? { squareSuffix: listSquareSuffix } : undefined
          );
          setSavedListId(activity.id);
          setEditingActivityId(null);
          toast.success('已更新');
          void mutateActivities();
        } else {
          const activity = await createActivity(
            {
              title: listTitle.trim(),
              description: listDescription.trim() || null,
              status: 'draft',
              startDate: startDate || null,
              endDate: endDate || null,
              productList,
              autoPublish,
              autoSettle,
            },
            jwtToken,
            listSquareSuffix
              ? { squareSuffix: listSquareSuffix }
              : isSquareMode && suffix
                ? { squareSuffix: suffix }
                : undefined
          );
          setSavedListId(activity.id);
          toast.success('已儲存');
          // 樂觀更新：立即把新草稿加入列表，不必等 refetch
          const newList: PurchaseList = {
            id: activity.id,
            title: activity.title,
            description: activity.description ?? undefined,
            items: [],
            createdAt: activity.createdAt,
            createdBy: activity.createdBy ?? '',
            startDate: activity.startDate ?? undefined,
            endDate: activity.endDate ?? undefined,
            status: 'draft',
          };
          setSavedLists(prev =>
            prev.some(l => l.id === activity.id) ? prev : [newList, ...prev]
          );
          void mutateActivities(undefined, { revalidate: true });
        }
        setIsFormVisible(false);
      } catch (err) {
        const msg = err instanceof Error ? err.message : '儲存失敗';
        toast.error(msg);
        setSaveError(msg);
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // 以下為 Group 模式：使用 GroupStorage
    let activeStorage: GroupStorage;
    try {
      activeStorage = new GroupStorage(groupId);
    } catch (error) {
      const errorMsg = `系統初始化失敗: ${error instanceof Error ? error.message : String(error)}`;
      console.error('[HoldPage] 創建 GroupStorage 失敗:', error, {
        groupId,
        currentUrl: window.location.href,
      });
      setSaveError(errorMsg);
      toast.error('系統初始化失敗，請稍後再試');
      return;
    }

    // 防止重複提交
    if (isSaving) {
      console.warn('[HoldPage] 正在儲存中，忽略重複提交');
      return;
    }

    setIsSaving(true);
    // 清除之前的錯誤
    setSaveError(null);

    try {
      // 使用 groupId 作為 listId
      const listId = groupId;

      // 過濾掉沒有名稱的項目（不儲存）
      const itemsWithName = items.filter(item => item.name && item.name.trim());

      // 驗證有名稱的項目：單一商品檢查價格/數量，多規格檢查總量與規格
      const validationErrors: string[] = [];
      const validItems: PurchaseListItem[] = itemsWithName.map(item => {
        const itemName = item.name.trim();
        if (isPurchaseListItemVariant(item)) {
          const maxTotal =
            typeof item.maxTotalQuantity === 'number' &&
            !isNaN(item.maxTotalQuantity) &&
            item.maxTotalQuantity > 0
              ? item.maxTotalQuantity
              : 0;
          if (maxTotal === 0) {
            validationErrors.push(`項目「${itemName}」的總數量上限必須大於 0`);
          }
          const validSpecs = (item.specs || []).map(spec => {
            const p =
              typeof spec.price === 'number' &&
              !isNaN(spec.price) &&
              spec.price >= 0
                ? spec.price
                : 0;
            if (!spec.name?.trim()) {
              validationErrors.push(`項目「${itemName}」的規格需填寫名稱`);
            }
            return { ...spec, name: spec.name?.trim() ?? '', price: p };
          });
          const hasValidSpec = validSpecs.some(s => s.name && s.price >= 0);
          if (!hasValidSpec && validSpecs.length > 0) {
            validationErrors.push(
              `項目「${itemName}」至少需有一個規格具備名稱與價格`
            );
          }
          return {
            ...item,
            name: itemName,
            maxTotalQuantity: maxTotal,
            specs: validSpecs,
          };
        }
        // 單一商品
        let validPrice = 0;
        if (
          typeof (item as PurchaseListItemSimple).price === 'number' &&
          !isNaN((item as PurchaseListItemSimple).price) &&
          (item as PurchaseListItemSimple).price >= 0
        ) {
          validPrice = (item as PurchaseListItemSimple).price;
        } else {
          validationErrors.push(
            `項目「${itemName}」的價格格式錯誤（必須為大於等於 0 的數字）`
          );
        }
        let validQuantity = 0;
        if (
          typeof (item as PurchaseListItemSimple).quantity === 'number' &&
          !isNaN((item as PurchaseListItemSimple).quantity) &&
          (item as PurchaseListItemSimple).quantity >= 0 &&
          Number.isInteger((item as PurchaseListItemSimple).quantity)
        ) {
          validQuantity = (item as PurchaseListItemSimple).quantity;
        } else {
          validationErrors.push(
            `項目「${itemName}」的數量格式錯誤（必須為大於等於 0 的整數）`
          );
        }
        // 數量 0 或留空表示不限制，不因數量為 0 報錯
        if (validPrice === 0 && validQuantity === 0) {
          validationErrors.push(`項目「${itemName}」的價格和數量不能同時為 0`);
        } else if (validPrice === 0) {
          validationErrors.push(`項目「${itemName}」的價格不能為 0`);
        }
        return {
          ...item,
          name: itemName,
          price: validPrice,
          quantity: validQuantity,
        } as PurchaseListItemSimple;
      });

      // 如果有驗證錯誤，顯示錯誤訊息
      if (validationErrors.length > 0) {
        const errorMsg = validationErrors.join('\n');
        console.error('[HoldPage] 驗證失敗:', errorMsg);
        setSaveError(errorMsg);
        toast.error('請修正以下錯誤：\n' + errorMsg);
        return;
      }

      // 確保至少有一個有效項目
      if (validItems.length === 0) {
        const errorMsg = '請至少新增一個有效的項目（需包含名稱、價格和數量）';
        console.error('[HoldPage] 驗證失敗:', errorMsg);
        setSaveError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      console.log('[HoldPage] 驗證通過，準備儲存', {
        listTitle: listTitle.trim(),
        itemsCount: validItems.length,
        listId,
        hasStorage: !!activeStorage,
        storageType: activeStorage?.constructor?.name,
      });

      // 嘗試取得 JWT Token 並保存到資料庫
      const jwtToken = getJwtToken();
      let activityId: string | null = null;

      // 準備商品清單（轉換為 API 格式：單一商品或多規格）
      const productList = validItems.map((item, index) => {
        const id = item.id || `product_${Date.now()}_${index}`;
        if (isPurchaseListItemVariant(item)) {
          return {
            id,
            name: item.name,
            maxTotalQuantity: item.maxTotalQuantity,
            specs: item.specs.map(s => ({
              id: s.id,
              name: s.name,
              price: Math.round(s.price),
            })),
            includePrepaidOnPublish: item.includePrepaidOnPublish !== false,
          };
        }
        return {
          id,
          name: item.name,
          price: Math.round(item.price),
          quantity: item.quantity,
        };
      });

      // 如果是編輯模式（editingActivityId 存在），使用 updateActivity
      if (editingActivityId && jwtToken) {
        try {
          const { updateActivity } = await import(
            '../../api/liteActivitiesClient'
          );
          // 先取得原活動狀態，編輯時保持原狀態
          const existingList = savedLists.find(
            list => list.id === editingActivityId
          );
          const updateData: UpdateActivityRequest = {
            title: listTitle.trim(),
            description: listDescription.trim() || null,
            startDate: startDate || null,
            endDate: endDate || null,
            productList, // 商品清單存儲在 jsonb 中
            autoPublish,
            autoSettle,
          };
          // 編輯時保持原狀態（不改變 status），除非明確指定
          if (
            existingList?.status &&
            ['draft', 'active', 'inactive'].includes(existingList.status)
          ) {
            updateData.status = existingList.status as
              | 'draft'
              | 'active'
              | 'inactive';
          }

          const activity = await updateActivity(
            editingActivityId,
            updateData,
            jwtToken
          );

          activityId = activity.id;
          console.log('[HoldPage] ✅ 已更新活動和商品到資料庫', {
            activityId: activity.id,
            groupId: activity.groupId,
            productsCount: productList.length,
          });
        } catch (dbError) {
          console.error('[HoldPage] 更新到資料庫失敗', {
            error: dbError instanceof Error ? dbError.message : String(dbError),
          });
          // 檢查是否為發佈上限錯誤
          const errorCode = (dbError as Error & { code?: string })?.code;
          if (errorCode === 'MONTHLY_PUBLISH_LIMIT') {
            toast.error('已達本月發佈上限，無法發佈活動');
          } else {
            toast.error('更新活動失敗，請稍後再試');
          }
          setIsSaving(false);
          return;
        }
      } else if (jwtToken && groupId) {
        // 如果是新建模式，使用 createActivity
        try {
          console.log('[HoldPage] 準備保存到資料庫', {
            groupId,
            title: listTitle.trim(),
            hasJwtToken: !!jwtToken,
          });

          // 調用後端 API 保存到資料庫（包含 productList）
          const activity = await createActivity(
            {
              title: listTitle.trim(),
              description: listDescription.trim() || null,
              status: 'draft',
              startDate: startDate || null,
              endDate: endDate || null,
              productList, // 商品清單存儲在 jsonb 中
              autoPublish,
              autoSettle,
            },
            jwtToken
          );

          activityId = activity.id;
          console.log('[HoldPage] ✅ 已保存活動和商品到資料庫', {
            activityId: activity.id,
            groupId: activity.groupId,
            productsCount: productList.length,
          });
        } catch (dbError) {
          console.warn('[HoldPage] 保存到資料庫失敗，將只保存到 localStorage', {
            error: dbError instanceof Error ? dbError.message : String(dbError),
          });
          // 繼續執行，保存到 localStorage 作為備份
        }
      } else {
        console.warn(
          '[HoldPage] 沒有 JWT Token 或 groupId，將只保存到 localStorage',
          {
            hasJwtToken: !!jwtToken,
            hasGroupId: !!groupId,
          }
        );
      }

      // 如果是編輯模式，保持原狀態；否則預設為 draft（未發佈）
      const finalStatus = editingActivityId
        ? savedLists.find(list => list.id === editingActivityId)?.status ||
          'draft'
        : 'draft';

      const purchaseList: PurchaseList = {
        id: activityId || listId, // 如果有 activityId，使用它；否則使用 groupId
        title: listTitle.trim(),
        description: listDescription.trim() || undefined,
        items: validItems,
        createdAt: editingActivityId
          ? savedLists.find(list => list.id === editingActivityId)?.createdAt ||
            new Date().toISOString()
          : new Date().toISOString(), // 編輯時保持原建立時間
        createdBy: profile?.userId || 'anonymous',
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        status: finalStatus,
      };

      // 儲存到 GroupStorage（作為備份，直接使用 groupId 作為 key）
      console.log('[HoldPage] 開始儲存到 GroupStorage', {
        groupId,
        listTitle: listTitle.trim(),
        itemsCount: validItems.length,
        hasStorage: !!activeStorage,
        storageType: activeStorage?.constructor?.name,
        purchaseListKeys: Object.keys(purchaseList),
        activityId,
      });

      try {
        // 直接使用 groupId 作為存儲 key
        const storageKey = groupId;
        console.log('[HoldPage] 準備儲存，storageKey:', storageKey);
        activeStorage.setItem(storageKey, purchaseList);
        console.log('[HoldPage] ✅ 已儲存到 GroupStorage', {
          groupId,
          storageKey,
          purchaseListSize: JSON.stringify(purchaseList).length,
          activityId,
        });

        // 驗證儲存是否成功
        const verifyItem = activeStorage.getItem<PurchaseList>(storageKey);
        if (!verifyItem) {
          throw new Error('儲存後驗證失敗：無法讀取剛儲存的資料');
        }
        console.log('[HoldPage] ✅ 儲存驗證成功', {
          verifyItemId: verifyItem.id,
          verifyItemTitle: verifyItem.title,
          verifyItemsCount: verifyItem.items.length,
        });
      } catch (storageError) {
        const errorMsg = `儲存到 GroupStorage 失敗: ${storageError instanceof Error ? storageError.message : String(storageError)}`;
        console.error('[HoldPage] 儲存到 GroupStorage 失敗', {
          error:
            storageError instanceof Error
              ? storageError.message
              : String(storageError),
          errorStack:
            storageError instanceof Error ? storageError.stack : undefined,
          groupId,
          listTitle: listTitle.trim(),
          itemsCount: validItems.length,
          hasStorage: !!activeStorage,
          storageType: activeStorage?.constructor?.name,
        });
        throw new Error(errorMsg);
      }

      // 儲存 groupId 以便後續使用（Group 模式備份用；Square 模式下 groupId 可能為 null）
      setSavedListId(groupId);

      // 產生分享連結：Square 優先，其次 Group，最後僅帶 activityId
      const baseUrl = window.location.origin.replace(/\/$/, '');
      const effectiveActivityId = activityId || listId;
      const shareLink = effectiveActivityId
        ? buildLiteJoinUrl({
            origin: baseUrl,
            groupId: groupId ?? undefined,
            suffix: listSquareSuffix ?? undefined,
            activityId: effectiveActivityId,
          })
        : baseUrl;

      console.log('[HoldPage] ✅ 儲存成功', {
        groupId,
        activityId,
        shareLink,
        listTitle: listTitle.trim(),
        itemsCount: validItems.length,
        isEdit: !!editingActivityId,
      });

      // 清除編輯狀態
      setEditingActivityId(null);

      toast.success(editingActivityId ? '活動已更新！' : '清單已儲存！', {
        duration: 2000,
      });

      // 儲存成功後隱藏表單
      setIsFormVisible(false);

      // 如果成功保存到資料庫，立即將新活動加入到 savedLists（不需要等待 API 重新載入）
      if (activityId) {
        const newActivity: PurchaseList = {
          id: activityId,
          title: purchaseList.title,
          description: purchaseList.description,
          items: purchaseList.items,
          createdAt: purchaseList.createdAt,
          createdBy: purchaseList.createdBy,
          startDate: purchaseList.startDate,
          endDate: purchaseList.endDate,
          status: purchaseList.status,
        };
        // 不在 setState updater 內呼叫 mutate，避免「Cannot update while rendering」警告
        const exists = savedLists.some(list => list.id === activityId);
        const nextAfterSave = exists
          ? savedLists.map(list =>
              list.id === activityId ? newActivity : list
            )
          : [newActivity, ...savedLists];
        setSavedLists(nextAfterSave);
        void mutateActivities(nextAfterSave, { revalidate: false });
      }

      // 背景重新驗證，確保與後端同步（不阻塞 UI）
      void mutateActivities();
    } catch (error) {
      console.error('[HoldPage] 儲存清單失敗:', error);
      logErrorToBackend(error, {
        context: {
          action: 'saveList',
          listTitle: listTitle.trim(),
          itemsLength: items.length,
        },
      });
      // 顯示具體錯誤訊息
      const errorMessage =
        error instanceof Error ? error.message : '儲存失敗，請稍後再試';
      setSaveError(errorMessage);
      toast.error(errorMessage);
      console.error('[HoldPage] 儲存錯誤詳情:', {
        error,
        errorMessage,
        items: items.map((item, index) => ({
          index: index + 1,
          name: item.name,
          ...(isPurchaseListItemVariant(item)
            ? { maxTotalQuantity: item.maxTotalQuantity }
            : {
                price: (item as PurchaseListItemSimple).price,
                quantity: (item as PurchaseListItemSimple).quantity,
              }),
        })),
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * 發佈活動（將狀態改為 active，發送 Flex Message，並產生分享連結）
   */
  const handlePublishList = async (targetGroupId: string) => {
    if (!targetGroupId) {
      toast.error('無法發佈活動');
      return;
    }

    try {
      // 從 savedLists 中找到對應的活動
      const existingList = savedLists.find(list => list.id === targetGroupId);
      if (!existingList) {
        toast.error('找不到此活動');
        return;
      }
      if (existingList.status !== 'draft') {
        toast.error('僅能發佈未發佈的活動');
        return;
      }

      // 嘗試取得 JWT Token 並更新資料庫
      const jwtToken = getJwtToken();
      if (!jwtToken) {
        toast.error('請先登入');
        return;
      }

      // 檢查活動 ID 是否是 UUID（來自資料庫）
      const isActivityIdFromDb =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          existingList.id
        );

      let publishShare:
        | {
            sharePath: string;
            shortUrl?: string;
            shareMessageText: string;
          }
        | undefined;
      if (isActivityIdFromDb) {
        // 調用後端 API 更新狀態為 active；後端回傳分享用 sharePath + shareMessageText（純文字）
        try {
          const { updateActivity } = await import(
            '../../api/liteActivitiesClient'
          );
          const updated = await updateActivity(
            existingList.id,
            { status: 'active' },
            jwtToken,
            listSquareSuffix ? { squareSuffix: listSquareSuffix } : undefined
          );
          publishShare = updated.publishShare;
          console.log(
            '[HoldPage] ✅ 已更新資料庫活動狀態為 active',
            publishShare ? '，已帶分享內容' : ''
          );
        } catch (dbError) {
          console.error('[HoldPage] 更新資料庫活動狀態失敗', {
            error: dbError instanceof Error ? dbError.message : String(dbError),
          });
          // 檢查是否為發佈上限錯誤
          const errorCode = (dbError as Error & { code?: string })?.code;
          if (errorCode === 'MONTHLY_PUBLISH_LIMIT') {
            toast.error('已達本月發佈上限，無法發佈活動', { duration: 3000 });
          } else if (errorCode === 'LITE_ACTIVITY_WRONG_SCOPE') {
            toast.error(
              (dbError as Error).message || '此活動不屬於當前場域，無法發佈',
              { duration: 4000 }
            );
          } else {
            toast.error('發佈失敗，請稍後再試', { duration: 2000 });
          }
          return;
        }
      } else {
        // 如果不是資料庫活動，只更新本地狀態
        console.warn('[HoldPage] 活動不是來自資料庫，無法發送 Flex Message');
      }

      // 更新本地狀態
      setSavedLists(prev => {
        return prev.map(list => {
          if (list.id === targetGroupId) {
            return {
              ...list,
              status: 'active' as const,
            };
          }
          return list;
        });
      });

      // 分享連結：優先 shortUrl 組出前端短網址（/g/:shortCode），否則 sharePath 或 buildLiteJoinUrl 長網址
      const baseUrl = window.location.origin.replace(/\/$/, '');
      const shareLink = publishShare?.shortUrl
        ? buildLiteJoinUrl({
            shortUrl: publishShare.shortUrl,
            origin: baseUrl,
            groupId: groupId ?? undefined,
            suffix: listSquareSuffix ?? undefined,
            activityId: existingList.id,
          })
        : publishShare?.sharePath
          ? `${baseUrl}${publishShare.sharePath.startsWith('/') ? '' : '/'}${publishShare.sharePath}`
          : buildLiteJoinUrl({
              origin: baseUrl,
              groupId: groupId ?? undefined,
              suffix: listSquareSuffix ?? undefined,
              activityId: existingList.id,
            });

      toast.success('活動已發佈', { duration: 2000 });
      setShareDialogLink(shareLink);
      setShareDialogTitle(existingList.title || '活動連結');
      setShareDialogText(
        normalizeShareText(
          publishShare?.shareMessageText ??
            `${existingList.title || '活動連結'}\n${shareLink}`
        )
      );
      setShareDialogOpen(true);
      // 不在此處自動呼叫 shareTargetPicker / navigator.share：手機上需在「使用者手勢」內觸發才會跳出分享選單，改由 Dialog 內「分享」按鈕觸發

      // 使用 SWR mutate 立即更新 UI
      await mutateActivities();
    } catch (error) {
      console.error('[HoldPage] 發佈活動失敗:', error);
      toast.error('發佈失敗，請稍後再試', { duration: 2000 });
    }
  };

  /**
   * 由分享 Dialog 內「分享到 LINE」按鈕觸發；與 Join 送出一致，僅分享到 LINE。
   * LIFF 內用 shareTargetPicker，否則用 line.me/R/share 開啟 LINE 分享。
   */
  const handleShareToLineFromDialog = useCallback(() => {
    const shareText = normalizeShareText(
      (shareDialogText && shareDialogText.trim()) ||
        `${shareDialogTitle || '活動連結'}\n${shareDialogLink}`
    );
    const picker =
      liff &&
      'shareTargetPicker' in liff &&
      typeof (
        liff as unknown as {
          shareTargetPicker?: (m: unknown[]) => Promise<unknown>;
        }
      ).shareTargetPicker === 'function'
        ? (
            liff as unknown as {
              shareTargetPicker: (m: unknown[]) => Promise<unknown>;
            }
          ).shareTargetPicker
        : null;
    if (picker) {
      picker([{ type: 'text', text: shareText }])
        .then(result => {
          if (result !== undefined && result !== null) {
            toast.success('已分享至 LINE', { duration: 2000 });
          }
        })
        .catch(() => {
          const lineShareUrl = `https://line.me/R/share?text=${encodeURIComponent(shareText)}`;
          window.open(lineShareUrl, '_blank', 'noopener,noreferrer');
          toast.success('已開啟 LINE 分享', { duration: 2000 });
        });
    } else {
      const lineShareUrl = `https://line.me/R/share?text=${encodeURIComponent(shareText)}`;
      window.open(lineShareUrl, '_blank', 'noopener,noreferrer');
      toast.success('已開啟 LINE 分享', { duration: 2000 });
    }
  }, [shareDialogText, shareDialogTitle, shareDialogLink, liff]);

  /**
   * 編輯活動（從 API 載入活動資料到表單）
   */
  const handleEditList = async (targetGroupId: string) => {
    if (!targetGroupId) {
      toast.error('系統尚未準備完成，請稍候');
      return;
    }

    // 檢查活動是否已發佈（進行中）
    const targetList = savedLists.find(list => list.id === targetGroupId);
    if (targetList?.status === 'active') {
      toast.error('已發佈的活動無法編輯');
      return;
    }

    try {
      const jwtToken = getJwtToken();
      if (!jwtToken) {
        toast.error('請先登入');
        return;
      }

      // 從 savedLists 中找到對應的活動
      const savedList = savedLists.find(list => list.id === targetGroupId);
      if (!savedList) {
        toast.error('找不到此活動');
        return;
      }

      // 如果活動 ID 是 UUID（來自資料庫），從 API 載入完整資料
      const isActivityIdFromDb =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          savedList.id
        );

      if (isActivityIdFromDb) {
        try {
          const { getActivity } = await import(
            '../../api/liteActivitiesClient'
          );
          const activity = await getActivity(
            savedList.id,
            jwtToken,
            listSquareSuffix ? { squareSuffix: listSquareSuffix } : undefined
          );

          // 載入活動資料到表單
          setListTitle(activity.title || '');
          setListDescription(activity.description || '');
          // 將 productList 轉換為 items 格式（支援單一商品與多規格）
          const itemsFromProductList = (activity.productList || []).map(
            (product: {
              id: string;
              name: string;
              price?: number;
              quantity?: number;
              maxTotalQuantity?: number;
              specs?: Array<{ id: string; name: string; price: number }>;
            }) => {
              if (
                product.specs &&
                Array.isArray(product.specs) &&
                product.specs.length > 0
              ) {
                return {
                  id: product.id,
                  name: product.name,
                  maxTotalQuantity: product.maxTotalQuantity ?? 0,
                  specs: product.specs.map(s => ({
                    id: s.id,
                    name: s.name,
                    price: s.price ?? 0,
                  })),
                  includePrepaidOnPublish:
                    (product as { includePrepaidOnPublish?: boolean })
                      .includePrepaidOnPublish !== false,
                } as PurchaseListItemVariant;
              }
              return {
                id: product.id,
                name: product.name,
                price: product.price ?? 0,
                quantity: product.quantity ?? 0,
              } as PurchaseListItemSimple;
            }
          );
          if (itemsFromProductList.length > 0) {
            setItems(itemsFromProductList);
            // 編輯活動時項目一律收合，僅新增項目時才展開
            setExpandedItems(new Set());
          } else {
            const firstItem = createEmptyPurchaseListItem();
            setItems([firstItem]);
            setExpandedItems(new Set());
          }
          // 將 UTC ISO 字串轉換為 datetime-local 格式（YYYY-MM-DDTHH:mm）
          if (activity.startDate) {
            const date = new Date(activity.startDate);
            // 轉換為台北時間（UTC+8）
            const taipeiDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
            const taipeiYear = taipeiDate.getUTCFullYear();
            const taipeiMonth = String(taipeiDate.getUTCMonth() + 1).padStart(
              2,
              '0'
            );
            const taipeiDay = String(taipeiDate.getUTCDate()).padStart(2, '0');
            const taipeiHours = String(taipeiDate.getUTCHours()).padStart(
              2,
              '0'
            );
            const taipeiMinutes = String(taipeiDate.getUTCMinutes()).padStart(
              2,
              '0'
            );
            setStartDate(
              `${taipeiYear}-${taipeiMonth}-${taipeiDay}T${taipeiHours}:${taipeiMinutes}`
            );
          } else {
            setStartDate('');
          }
          if (activity.endDate) {
            const date = new Date(activity.endDate);
            // 轉換為台北時間（UTC+8）
            const taipeiDate = new Date(date.getTime() + 8 * 60 * 60 * 1000);
            const taipeiYear = taipeiDate.getUTCFullYear();
            const taipeiMonth = String(taipeiDate.getUTCMonth() + 1).padStart(
              2,
              '0'
            );
            const taipeiDay = String(taipeiDate.getUTCDate()).padStart(2, '0');
            const taipeiHours = String(taipeiDate.getUTCHours()).padStart(
              2,
              '0'
            );
            const taipeiMinutes = String(taipeiDate.getUTCMinutes()).padStart(
              2,
              '0'
            );
            setEndDate(
              `${taipeiYear}-${taipeiMonth}-${taipeiDay}T${taipeiHours}:${taipeiMinutes}`
            );
          } else {
            setEndDate('');
          }
          setAutoPublish(activity.autoPublish || false);
          setAutoSettle(activity.autoSettle || false);
          setEditingActivityId(activity.id); // 記錄正在編輯的活動 ID

          // 如果 autoPublish 或 autoSettle 為 true，但沒有日期時間，清空它們
          if (
            (activity.autoPublish || activity.autoSettle) &&
            !activity.startDate &&
            !activity.endDate
          ) {
            setAutoPublish(false);
            setAutoSettle(false);
          }

          // 如果活動已發佈（進行中），禁用編輯
          if (activity.status === 'active') {
            toast.error('已發佈的活動無法編輯');
            setEditingActivityId(null);
            setIsFormVisible(false);
            return;
          }
        } catch (apiError) {
          console.warn(
            '[HoldPage] 從 API 載入活動失敗，使用本地資料',
            apiError
          );
          // 如果 API 載入失敗，使用本地資料
          setListTitle(savedList.title || '');
          setListDescription(savedList.description || '');
          setItems(savedList.items || []);
          // 編輯活動時項目一律收合
          setExpandedItems(new Set());
          setStartDate(savedList.startDate || '');
          setEndDate(savedList.endDate || '');
          setEditingActivityId(savedList.id);
        }
      } else {
        // 如果不是資料庫活動，使用本地資料
        setListTitle(savedList.title || '');
        setListDescription(savedList.description || '');
        setItems(savedList.items || []);
        setExpandedItems(new Set());
        setStartDate(savedList.startDate || '');
        setEndDate(savedList.endDate || '');
        setEditingActivityId(null); // 本地活動沒有 activityId
      }

      setIsFormVisible(true);
      // 滾動到表單頂部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('[HoldPage] 載入活動失敗:', error);
      toast.error('載入活動失敗');
    }
  };

  /**
   * 新增活動（清空表單）
   */
  const handleNewList = () => {
    setListTitle('');
    setListDescription('');
    const firstItem = createEmptyPurchaseListItem();
    setItems([firstItem]);
    setStartDate('');
    setEndDate('');
    setAutoPublish(false);
    setAutoSettle(false);
    setSavedListId(null);
    setSaveError(null);
    setEditingActivityId(null); // 清除編輯狀態
    setIsFormVisible(true);
    setExpandedItems(new Set([firstItem.id]));
    // 滾動到表單頂部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** 將辨識草稿套用到表單（不寫入 DB） */
  const applyRecognizeDraft = (draft: {
    title: string | null;
    description: string | null;
    productList: Array<{
      id: string;
      name: string;
      price?: number;
      quantity?: number;
      maxTotalQuantity?: number;
      specs?: Array<{ id: string; name: string; price: number }>;
    }>;
  }) => {
    setListTitle(draft.title?.trim() || '');
    setListDescription(draft.description?.trim() || '');
    const itemsFromProductList = (draft.productList || []).map(product => {
      if (
        product.specs &&
        Array.isArray(product.specs) &&
        product.specs.length > 0
      ) {
        return {
          id: product.id,
          name: product.name,
          maxTotalQuantity: product.maxTotalQuantity ?? 0,
          specs: product.specs.map(s => ({
            id: s.id,
            name: s.name,
            price: s.price ?? 0,
          })),
          includePrepaidOnPublish:
            (product as { includePrepaidOnPublish?: boolean })
              .includePrepaidOnPublish !== false,
        } as PurchaseListItemVariant;
      }
      return {
        id: product.id,
        name: product.name,
        price: product.price ?? 0,
        quantity: product.quantity ?? 0,
      } as PurchaseListItemSimple;
    });
    if (itemsFromProductList.length > 0) {
      setItems(itemsFromProductList);
      setExpandedItems(new Set());
    } else {
      setItems([createEmptyPurchaseListItem()]);
      setExpandedItems(new Set());
    }
    setEditingActivityId(null);
    setMenuRecognizeOpen(false);
    setMenuRecognizeFiles([]);
    setRecognizeError(null);
    setIsFormVisible(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** 送出菜單辨識（圖片讀取生成活動上限 3 張） */
  const handleRecognizeSubmit = async () => {
    if (menuRecognizeFiles.length === 0) {
      setRecognizeError('請至少選擇一張菜單圖片');
      return;
    }
    if (menuRecognizeFiles.length > 3) {
      setRecognizeError('最多 3 張圖片');
      return;
    }
    const jwtToken = getJwtToken();
    if (!jwtToken) {
      toast.error('請先登入');
      return;
    }
    setIsRecognizing(true);
    setRecognizeError(null);
    try {
      const draft = await recognizeMenuImages(menuRecognizeFiles, jwtToken);
      toast.success('辨識完成，請確認內容後儲存');
      applyRecognizeDraft(draft);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '菜單辨識失敗，請稍後再試';
      setRecognizeError(message);
      toast.error(message);
    } finally {
      setIsRecognizing(false);
    }
  };

  // 移除側滑處理邏輯，改用 ActionSheet
  // 滑動手勢已由 ActionSheet 取代

  /**
   * 打開 ActionSheet
   */
  const handleOpenActionSheet = useCallback((listId: string) => {
    setSelectedActivityId(listId);
    setActionSheetOpened(true);
  }, []);

  /**
   * 刪除活動（軟刪除）
   */
  const handleDeleteList = async (targetGroupId: string) => {
    setDeleteDialogOpened(true);
    setSelectedActivityId(targetGroupId);
  };

  /**
   * 複製活動
   */
  const handleDuplicateList = async (targetGroupId: string) => {
    if (!targetGroupId) {
      toast.error('系統尚未準備完成，請稍候');
      return;
    }

    try {
      const jwtToken = getJwtToken();
      if (!jwtToken) {
        toast.error('請先登入');
        return;
      }

      // 複製活動
      const duplicated = await duplicateActivity(
        targetGroupId,
        jwtToken,
        listSquareSuffix ? { squareSuffix: listSquareSuffix } : undefined
      );

      // 立即更新「已儲存的活動」區塊（optimistic）
      const duplicatedList: PurchaseList = {
        id: duplicated.id,
        title: duplicated.title,
        description: duplicated.description || undefined,
        items: [],
        createdAt: duplicated.createdAt,
        createdBy: duplicated.createdBy || '',
        startDate: duplicated.startDate || undefined,
        endDate: duplicated.endDate || undefined,
        status:
          (duplicated.status as 'draft' | 'active' | 'inactive') || 'draft',
      };

      const nextAfterDuplicate = [duplicatedList, ...savedLists];
      setSavedLists(nextAfterDuplicate);
      void mutateActivities(nextAfterDuplicate, { revalidate: false });

      toast.success('活動已複製');

      // 背景重新驗證，確保與後端同步（不阻塞 UI）
      void mutateActivities();

      // 關閉 Drawer
      setActionSheetOpened(false);
    } catch (error: unknown) {
      console.error('[HoldPage] 複製活動失敗:', error);
      const msg =
        error instanceof Error ? error.message : '複製活動失敗，請稍後再試';
      toast.error(msg, { duration: msg.length > 30 ? 5000 : 2000 });
    }
  };

  /**
   * 確認刪除
   */
  const handleConfirmDelete = async () => {
    if (!selectedActivityId) return;

    const targetGroupId = selectedActivityId;
    setDeleteDialogOpened(false);

    if (!targetGroupId) {
      toast.error('系統尚未準備完成，請稍候');
      return;
    }

    try {
      const jwtToken = getJwtToken();
      if (!jwtToken) {
        toast.error('請先登入');
        return;
      }

      // 檢查活動 ID 是否是 UUID（來自資料庫）
      const savedList = savedLists.find(list => list.id === targetGroupId);
      if (!savedList) {
        toast.error('找不到此活動');
        return;
      }

      const isActivityIdFromDb =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          savedList.id
        );

      // 立即從 UI 移除（optimistic）；不在 setState updater 內呼叫 mutate，避免「Cannot update while rendering」警告
      const nextLists = savedLists.filter(list => list.id !== targetGroupId);
      setSavedLists(nextLists);
      void mutateActivities(nextLists, { revalidate: false });

      if (isActivityIdFromDb) {
        // 調用軟刪除 API
        await deleteActivity(
          savedList.id,
          jwtToken,
          listSquareSuffix ? { squareSuffix: listSquareSuffix } : undefined
        );
      }

      // 背景重新驗證，確保與後端同步（不阻塞 UI）
      void mutateActivities();

      toast.success('活動已刪除');

      // 如果刪除的是當前正在編輯的活動，清空表單
      if (targetGroupId === groupId || targetGroupId === savedListId) {
        setListTitle('');
        setListDescription('');
        setItems([]);
        setSavedListId(null);
        setEditingActivityId(null);
      }
    } catch (error) {
      console.error('[HoldPage] 刪除活動失敗:', error);
      const msg =
        error instanceof Error ? error.message : '刪除失敗，請稍後再試';
      toast.error(msg, { duration: msg.length > 30 ? 5000 : 2000 });

      // 回滾：重新驗證取得最新列表
      void mutateActivities();
    }
  };

  // 計算總金額（保留以備未來使用）
  // const totalAmount = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  // const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

  const draftLists = useMemo(
    () => savedLists.filter(list => list.status === 'draft'),
    [savedLists]
  );
  const savedListSectionNode = useMemo(() => {
    return (
      <>
        <MobileCardTitle className="mt-6">已儲存的活動</MobileCardTitle>
        {draftLists.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground px-1">
            尚無草稿，點上方「新增活動」或「從菜單建立」開始建立。
          </p>
        ) : (
          <MobileList strong inset>
            {draftLists.map(list => {
              const dateRange =
                list.startDate && list.endDate
                  ? `${new Date(list.startDate).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })} ~ ${new Date(list.endDate).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                  : list.startDate
                    ? new Date(list.startDate).toLocaleString('zh-TW', {
                        timeZone: 'Asia/Taipei',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : list.endDate
                      ? new Date(list.endDate).toLocaleString('zh-TW', {
                          timeZone: 'Asia/Taipei',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '未設定日期時間';
              const statusPrefix =
                list.status === 'draft'
                  ? '未發佈 · '
                  : list.status === 'inactive'
                    ? '已結束 · '
                    : '';
              return (
                <MobileListItem
                  key={list.id}
                  title={<span>{list.title || '未命名活動'}</span>}
                  subtitle={`${statusPrefix}${dateRange}`}
                  onClick={() => handleOpenActionSheet(list.id)}
                  showArrow={true}
                />
              );
            })}
          </MobileList>
        )}
      </>
    );
  }, [draftLists, handleOpenActionSheet]);

  const handleBack = () => {
    navigate(
      getLiteReturnPath({
        suffix: isSquareMode && suffix ? suffix : undefined,
        groupId: getGroupIdFromUrl(),
      })
    );
  };

  return (
    <>
      <CreateActivityFormLayout
        title={editingActivityId ? '編輯活動' : '建立活動'}
        onBack={handleBack}
        contentClassName="space-y-4"
      >
        {/* 在頁面頂部顯示儲存錯誤（更明顯） */}
        {saveError && (
          <div className="p-4 bg-red-100 border-2 border-red-400 rounded-2xl shadow-lg">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 text-red-600 text-2xl">⚠️</div>
              <div className="flex-1">
                <p className="text-lg font-bold text-red-900 mb-1">儲存失敗</p>
                <p className="text-sm text-red-800 whitespace-pre-wrap break-words">
                  {saveError}
                </p>
              </div>
              <button
                onClick={() => setSaveError(null)}
                className="flex-shrink-0 text-red-600 hover:text-red-800 text-xl font-bold min-w-[32px] min-h-[32px] flex items-center justify-center"
                aria-label="關閉錯誤訊息"
              >
                ×
              </button>
            </div>
          </div>
        )}
        {!isFormVisible ? (
          <CreateActivityEntryView
            contentOnly
            onBack={handleBack}
            onNewActivity={handleNewList}
            showFromMenu={true}
            onFromMenu={() => {
              setMenuRecognizeFiles([]);
              setRecognizeError(null);
              setMenuRecognizeOpen(true);
            }}
            savedListSection={savedListSectionNode}
          />
        ) : (
          <MobileCard strong inset>
            {/* 合併的活動名稱和清單區塊 */}
            {(() => {
              // 檢查正在編輯的活動是否已發佈
              const editingList = editingActivityId
                ? savedLists.find(list => list.id === editingActivityId)
                : null;
              const isEditingActive = editingList?.status === 'active';

              return (
                <MobileList strong inset className="space-y-4 p-4">
                  <MobileInput
                    label="活動名稱"
                    type="text"
                    value={listTitle}
                    onChange={e => setListTitle(e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="例如：今天扣除訂單還有"
                    maxLength={100}
                    required
                    disabled={isEditingActive}
                    containerClassName="w-full"
                  />
                  <MobileTextarea
                    label="活動描述"
                    value={listDescription}
                    onChange={e => setListDescription(e.target.value)}
                    onFocus={handleInputFocus}
                    placeholder="例如：這次活動的詳細說明..."
                    maxLength={500}
                    disabled={isEditingActive}
                  />
                  <MobileListItem
                    title="顯示時間"
                    after={
                      <Switch
                        checked={autoPublish || autoSettle}
                        onCheckedChange={(checked: boolean) => {
                          setAutoPublish(checked);
                          setAutoSettle(checked);
                          // 如果取消勾選，清空時間
                          if (!checked) {
                            setStartDate('');
                            setEndDate('');
                          }
                        }}
                        disabled={isEditingActive}
                      />
                    }
                  />
                  {(autoPublish || autoSettle) && (
                    <>
                      <MobileInput
                        label="開始日期時間"
                        type="datetime-local"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                        onFocus={handleInputFocus}
                        disabled={isEditingActive}
                        containerClassName="w-full"
                        step="1"
                      />
                      <MobileInput
                        label="結束日期時間"
                        type="datetime-local"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        onFocus={handleInputFocus}
                        disabled={isEditingActive}
                        containerClassName="w-full"
                        step="1"
                      />
                    </>
                  )}

                  <MobileCardTitle className="mt-4">清單</MobileCardTitle>

                  {/* 已新增的項目列表 */}
                  {items.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {items.map(item => {
                        const isExpanded = expandedItems.has(item.id);
                        return (
                          <div
                            key={item.id}
                            data-item-id={item.id}
                            onDragOver={e => {
                              // 整個項目作為拖曳目標（接收拖曳）
                              if (draggedItemId && draggedItemId !== item.id) {
                                e.preventDefault();
                                handleDragOver(e, item.id);
                              }
                            }}
                            onDragLeave={handleDragLeave}
                            onDrop={e => {
                              // 整個項目可以接收拖曳
                              if (draggedItemId && draggedItemId !== item.id) {
                                e.preventDefault();
                                handleDrop(e, item.id);
                              }
                            }}
                            style={{
                              transform:
                                touchDragState?.itemId === item.id
                                  ? `translateY(${touchDragState.currentY - touchDragState.startY}px)`
                                  : undefined,
                              zIndex:
                                touchDragState?.itemId === item.id
                                  ? 50
                                  : undefined,
                            }}
                            className={`bg-gray-50 rounded-lg border transition-all ${
                              draggedItemId === item.id ||
                              touchDragState?.itemId === item.id
                                ? 'opacity-50 border-blue-400 shadow-lg'
                                : dragOverItemId === item.id ||
                                    touchDragState?.targetIndex ===
                                      items.findIndex(i => i.id === item.id)
                                  ? 'border-blue-400 border-2 bg-blue-50'
                                  : 'border-gray-200'
                            }`}
                          >
                            {/* 項目標題列（收合時顯示） */}
                            <div
                              className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 cursor-pointer touch-none"
                              onClick={() => {
                                setExpandedItems(prev => {
                                  const newSet = new Set(prev);
                                  if (newSet.has(item.id)) {
                                    newSet.delete(item.id);
                                  } else {
                                    newSet.add(item.id);
                                  }
                                  return newSet;
                                });
                              }}
                            >
                              {/* 拖曳圖標（手機和桌面都可用）- 只有這裡可以開始拖曳 */}
                              <div
                                className="text-gray-400 cursor-grab active:cursor-grabbing touch-none select-none"
                                draggable
                                onDragStart={e => {
                                  e.stopPropagation();
                                  handleDragStart(item.id);
                                }}
                                onDragEnd={e => {
                                  e.stopPropagation();
                                  handleDragEnd();
                                }}
                                onClick={e => e.stopPropagation()}
                                onTouchStart={e => {
                                  e.stopPropagation();
                                  handleTouchStart(e, item.id);
                                }}
                                onTouchMove={e => {
                                  e.stopPropagation();
                                  handleTouchMove(e);
                                }}
                                onTouchEnd={e => {
                                  e.stopPropagation();
                                  handleTouchEnd();
                                }}
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M6 3.5C6 4.32843 5.32843 5 4.5 5C3.67157 5 3 4.32843 3 3.5C3 2.67157 3.67157 2 4.5 2C5.32843 2 6 2.67157 6 3.5Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M6 8C6 8.82843 5.32843 9.5 4.5 9.5C3.67157 9.5 3 8.82843 3 8C3 7.17157 3.67157 6.5 4.5 6.5C5.32843 6.5 6 7.17157 6 8Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M6 12.5C6 13.3284 5.32843 14 4.5 14C3.67157 14 3 13.3284 3 12.5C3 11.6716 3.67157 11 4.5 11C5.32843 11 6 11.6716 6 12.5Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M13 3.5C13 4.32843 12.3284 5 11.5 5C10.6716 5 10 4.32843 10 3.5C10 2.67157 10.6716 2 11.5 2C12.3284 2 13 2.67157 13 3.5Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M13 8C13 8.82843 12.3284 9.5 11.5 9.5C10.6716 9.5 10 8.82843 10 8C10 7.17157 10.6716 6.5 11.5 6.5C12.3284 6.5 13 7.17157 13 8Z"
                                    fill="currentColor"
                                  />
                                  <path
                                    d="M13 12.5C13 13.3284 12.3284 14 11.5 14C10.6716 14 10 13.3284 10 12.5C10 11.6716 10.6716 11 11.5 11C12.3284 11 13 11.6716 13 12.5Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </div>
                              {/* 展開/收合圖標 */}
                              <button
                                className="text-gray-500 hover:text-gray-700 active:text-gray-900 min-w-[24px] min-h-[24px] flex items-center justify-center touch-manipulation"
                                onClick={e => {
                                  e.stopPropagation();
                                  setExpandedItems(prev => {
                                    const newSet = new Set(prev);
                                    if (newSet.has(item.id)) {
                                      newSet.delete(item.id);
                                    } else {
                                      newSet.add(item.id);
                                    }
                                    return newSet;
                                  });
                                }}
                              >
                                {isExpanded ? (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M18 15L12 9L6 15"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M6 9L12 15L18 9"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </button>
                              {/* 項目摘要（收合時顯示） */}
                              <div className="flex-1 flex items-center gap-2 sm:gap-4 text-sm sm:text-base flex-wrap">
                                <span className="font-medium text-gray-900">
                                  {item.name || '未命名商品'}
                                </span>
                                {isPurchaseListItemVariant(item) ? (
                                  <>
                                    <span className="text-gray-600">
                                      總量 ≤ {item.maxTotalQuantity}
                                    </span>
                                    {item.specs.map(s => (
                                      <span
                                        key={s.id}
                                        className="text-gray-500"
                                      >
                                        {s.name} NT${s.price}
                                      </span>
                                    ))}
                                  </>
                                ) : (
                                  <>
                                    {(item as PurchaseListItemSimple).price >
                                      0 && (
                                      <span className="text-gray-600">
                                        NT$
                                        {(item as PurchaseListItemSimple).price}
                                      </span>
                                    )}
                                    {(item as PurchaseListItemSimple).quantity >
                                      0 && (
                                      <span className="text-gray-600">
                                        ×
                                        {
                                          (item as PurchaseListItemSimple)
                                            .quantity
                                        }
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              {/* 刪除按鈕（已發佈的活動無法刪除項目） */}
                              {!isEditingActive && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleRemoveItem(item.id);
                                  }}
                                  className="text-red-600 hover:text-red-700 active:text-red-800 p-2 sm:p-3 min-w-[44px] min-h-[44px] flex items-center justify-center transition-colors touch-manipulation"
                                  title="刪除"
                                >
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-5 h-5"
                                  >
                                    <path
                                      d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {/* 項目詳細內容（展開時顯示） */}
                            {isExpanded && (
                              <div className="px-2 sm:px-3 pb-2 sm:pb-3 space-y-3 border-t border-gray-200 pt-3">
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-16 sm:w-20">
                                    {itemNameLabel}
                                  </label>
                                  <input
                                    type="text"
                                    value={item.name}
                                    onChange={e =>
                                      handleUpdateItem(
                                        item.id,
                                        'name',
                                        e.target.value
                                      )
                                    }
                                    placeholder={itemNamePlaceholder}
                                    className="flex-1 p-2 sm:p-3 text-base border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                                    disabled={isEditingActive}
                                    onClick={e => e.stopPropagation()}
                                    onFocus={e => {
                                      e.stopPropagation();
                                      handleInputFocus(e);
                                    }}
                                  />
                                </div>
                                {isPurchaseListItemVariant(item) ? (
                                  <>
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-16 sm:w-20">
                                        總量上限
                                      </label>
                                      <input
                                        type="number"
                                        min={1}
                                        value={item.maxTotalQuantity || ''}
                                        onChange={e => {
                                          const v = parseInt(
                                            e.target.value,
                                            10
                                          );
                                          handleUpdateVariantMaxTotal(
                                            item.id,
                                            isNaN(v) ? 0 : v
                                          );
                                        }}
                                        placeholder="例：40"
                                        disabled={isEditingActive}
                                        className="flex-1 p-2 sm:p-3 text-base border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                                        onClick={e => e.stopPropagation()}
                                      />
                                    </div>
                                    <div className="text-sm font-medium text-gray-700">
                                      規格（各規格單價不同，總數量共用上限）
                                    </div>
                                    {(() => {
                                      const isGenderVariant =
                                        item.specs.length === 2 &&
                                        item.specs.every(s =>
                                          ['男', '女'].includes(s.name)
                                        );
                                      return item.specs.map(spec => {
                                        const isFixedGenderSpec =
                                          isGenderVariant &&
                                          (spec.name === '男' ||
                                            spec.name === '女');
                                        return (
                                          <div
                                            key={spec.id}
                                            className="flex items-center gap-2 rounded-lg bg-white border border-gray-200 p-2"
                                          >
                                            {isFixedGenderSpec ? (
                                              <span className="flex-1 p-2 text-base text-gray-700">
                                                {spec.name}
                                              </span>
                                            ) : (
                                              <input
                                                type="text"
                                                value={spec.name}
                                                onChange={e =>
                                                  handleUpdateSpec(
                                                    item.id,
                                                    spec.id,
                                                    'name',
                                                    e.target.value
                                                  )
                                                }
                                                placeholder="規格名稱"
                                                disabled={isEditingActive}
                                                className="flex-1 p-2 text-base border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                                onClick={e =>
                                                  e.stopPropagation()
                                                }
                                              />
                                            )}
                                            <input
                                              type="text"
                                              value={
                                                spec.price > 0
                                                  ? String(spec.price)
                                                  : ''
                                              }
                                              onChange={e => {
                                                const val = e.target.value;
                                                if (
                                                  val === '' ||
                                                  /^\d*\.?\d*$/.test(val)
                                                ) {
                                                  handleUpdateSpec(
                                                    item.id,
                                                    spec.id,
                                                    'price',
                                                    val === ''
                                                      ? 0
                                                      : parseFloat(val) || 0
                                                  );
                                                }
                                              }}
                                              placeholder="單價"
                                              disabled={isEditingActive}
                                              className="w-20 p-2 text-base border border-gray-300 rounded focus:border-blue-500 focus:outline-none"
                                              onClick={e => e.stopPropagation()}
                                            />
                                            {!isEditingActive &&
                                              !isFixedGenderSpec && (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleRemoveSpec(
                                                      item.id,
                                                      spec.id
                                                    )
                                                  }
                                                  className="text-red-600 hover:text-red-700 p-2"
                                                  title="刪除規格"
                                                >
                                                  <svg
                                                    className="w-5 h-5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                    />
                                                  </svg>
                                                </button>
                                              )}
                                          </div>
                                        );
                                      });
                                    })()}
                                    {!isEditingActive &&
                                      !(
                                        item.specs.length === 2 &&
                                        item.specs.every(s =>
                                          ['男', '女'].includes(s.name)
                                        )
                                      ) && (
                                        <button
                                          type="button"
                                          onClick={() => handleAddSpec(item.id)}
                                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                        >
                                          + 新增規格
                                        </button>
                                      )}
                                    {isLitePrepaidShopType(currentShopType) && (
                                      <div className="flex items-center justify-start gap-2 pt-2 mt-2 border-t border-gray-100">
                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={
                                              item.includePrepaidOnPublish !==
                                              false
                                            }
                                            onChange={e =>
                                              handleSetIncludePrepaidOnPublish(
                                                item.id,
                                                e.target.checked
                                              )
                                            }
                                            disabled={isEditingActive}
                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                          />
                                          發佈時帶入預繳
                                        </label>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-16 sm:w-20">
                                        單價
                                      </label>
                                      <div className="flex-1">
                                        <input
                                          type="text"
                                          value={
                                            (item as PurchaseListItemSimple)
                                              .price > 0
                                              ? String(
                                                  (
                                                    item as PurchaseListItemSimple
                                                  ).price
                                                )
                                              : ''
                                          }
                                          onChange={e => {
                                            const value = e.target.value;
                                            if (
                                              value === '' ||
                                              /^-?\d*\.?\d*$/.test(value)
                                            ) {
                                              if (
                                                value === '' ||
                                                value === '-'
                                              ) {
                                                handleUpdateItem(
                                                  item.id,
                                                  'price',
                                                  0
                                                );
                                              } else {
                                                const numValue =
                                                  parseFloat(value);
                                                if (!isNaN(numValue)) {
                                                  handleUpdateItem(
                                                    item.id,
                                                    'price',
                                                    numValue
                                                  );
                                                }
                                              }
                                            }
                                          }}
                                          onBlur={e => {
                                            const value = e.target.value;
                                            if (
                                              value === '' ||
                                              value === '-' ||
                                              isNaN(parseFloat(value))
                                            ) {
                                              handleUpdateItem(
                                                item.id,
                                                'price',
                                                0
                                              );
                                            }
                                          }}
                                          placeholder="單價"
                                          disabled={isEditingActive}
                                          className={`w-full p-2 sm:p-3 text-base border rounded-lg focus:outline-none ${
                                            itemErrors[item.id]?.price
                                              ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                                              : 'border-gray-300 focus:border-blue-500'
                                          } ${isEditingActive ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                          onClick={e => e.stopPropagation()}
                                          onFocus={e => {
                                            e.stopPropagation();
                                            handleInputFocus(e);
                                          }}
                                        />
                                        {itemErrors[item.id]?.price && (
                                          <p className="text-xs text-red-600 mt-1">
                                            {itemErrors[item.id].price}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 sm:gap-3">
                                      <label className="text-sm font-medium text-gray-700 whitespace-nowrap w-16 sm:w-20">
                                        數量
                                      </label>
                                      <div className="flex-1">
                                        <input
                                          type="text"
                                          value={
                                            (item as PurchaseListItemSimple)
                                              .quantity > 0
                                              ? String(
                                                  (
                                                    item as PurchaseListItemSimple
                                                  ).quantity
                                                )
                                              : ''
                                          }
                                          onChange={e => {
                                            const value = e.target.value;
                                            if (
                                              value === '' ||
                                              /^\d+$/.test(value)
                                            ) {
                                              if (value === '') {
                                                handleUpdateItem(
                                                  item.id,
                                                  'quantity',
                                                  0
                                                );
                                              } else {
                                                const numValue = parseInt(
                                                  value,
                                                  10
                                                );
                                                if (
                                                  !isNaN(numValue) &&
                                                  numValue >= 0
                                                ) {
                                                  handleUpdateItem(
                                                    item.id,
                                                    'quantity',
                                                    numValue
                                                  );
                                                }
                                              }
                                            }
                                          }}
                                          placeholder="留空=不限制"
                                          disabled={isEditingActive}
                                          className={`w-full p-2 sm:p-3 text-base border rounded-lg focus:outline-none ${
                                            itemErrors[item.id]?.quantity
                                              ? 'border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200'
                                              : 'border-gray-300 focus:border-blue-500'
                                          } ${isEditingActive ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                          onClick={e => e.stopPropagation()}
                                          onFocus={e => {
                                            e.stopPropagation();
                                            handleInputFocus(e);
                                          }}
                                        />
                                        {itemErrors[item.id]?.quantity && (
                                          <p className="text-xs text-red-600 mt-1">
                                            {itemErrors[item.id].quantity}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    {!isEditingActive &&
                                      (isLitePrepaidShopType(currentShopType) ||
                                        !isProductShopType) && (
                                        <div className="flex flex-wrap items-center gap-3">
                                          <span className="text-sm text-gray-600">
                                            改為共用總量：
                                          </span>
                                          {isLitePrepaidShopType(
                                            currentShopType
                                          ) && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleConvertToGenderVariant(
                                                  item.id
                                                )
                                              }
                                              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                            >
                                              預繳專用（男、女）
                                            </button>
                                          )}
                                          {!isProductShopType && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleConvertToVariant(item.id)
                                              }
                                              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                            >
                                              分規格
                                            </button>
                                          )}
                                        </div>
                                      )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* 分隔線和 + 按鈕（放在最下面） */}
                  {!isEditingActive && (
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <button
                          type="button"
                          onClick={handleAddItem}
                          onTouchStart={e => {
                            e.stopPropagation();
                            handleAddItem();
                          }}
                          className="bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 active:bg-blue-100 font-semibold w-12 h-12 sm:w-10 sm:h-10 rounded-full transition-colors flex items-center justify-center text-xl min-w-[48px] min-h-[48px] touch-manipulation"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 儲存按鈕（在同一區塊） */}
                  {!isEditingActive && (
                    <div className="mt-4">
                      <Button
                        size="lg"
                        onClick={async e => {
                          console.log('[HoldPage] 🔥 按鈕 onClick 事件觸發', {
                            isSaving,
                            listTitle: listTitle.trim(),
                            itemsLength: items.length,
                          });
                          e.preventDefault();
                          e.stopPropagation();

                          if (isSaving) {
                            console.warn(
                              '[HoldPage] 🔥 正在儲存中，忽略重複點擊',
                              { isSaving }
                            );
                            return;
                          }

                          console.log('[HoldPage] 🔥 開始執行 handleSaveList');
                          await handleSaveList();
                        }}
                        disabled={isSaving}
                        className="w-full"
                      >
                        {isSaving ? '儲存中...' : '儲存'}
                      </Button>
                      {/* 顯示儲存錯誤訊息 */}
                      {saveError && (
                        <div className="mt-3">
                          <div className="p-4 bg-red-50 border-2 border-red-400 rounded-lg shadow-md">
                            <div className="flex items-start gap-2">
                              <div className="flex-shrink-0 text-red-600 text-xl">
                                ❌
                              </div>
                              <div className="flex-1">
                                <p className="text-base font-semibold text-red-800 mb-2">
                                  儲存失敗
                                </p>
                                <p className="text-sm text-red-700 whitespace-pre-wrap break-words">
                                  {saveError}
                                </p>
                                <div className="mt-2 text-xs text-red-600">
                                  <p>請檢查：</p>
                                  <ul className="list-disc list-inside mt-1 space-y-1">
                                    <li>活動名稱是否已填寫</li>
                                    <li>是否至少有一個項目</li>
                                    <li>項目的商品名稱、價格、數量是否完整</li>
                                    <li>URL 中是否包含 groupId 參數</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </MobileList>
              );
            })()}

            {savedListSectionNode}
          </MobileCard>
        )}
      </CreateActivityFormLayout>

      {/* Drawer - 活動操作選單 */}
      <Drawer open={actionSheetOpened} onOpenChange={setActionSheetOpened}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>選擇操作</DrawerTitle>
            <DrawerDescription className="sr-only">
              選擇要對此活動執行的操作
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 space-y-2">
            {selectedActivityId &&
              (() => {
                const list = savedLists.find(l => l.id === selectedActivityId);
                const isActive = list?.status === 'active';
                const isDraft = list?.status === 'draft';
                const isInactive = list?.status === 'inactive';

                // 「進行中」：只顯示複製
                if (isActive) {
                  return (
                    <Button
                      variant="default"
                      className="w-full"
                      onClick={() => {
                        setActionSheetOpened(false);
                        handleDuplicateList(selectedActivityId);
                      }}
                    >
                      複製
                    </Button>
                  );
                }

                // 「未發佈」draft：發佈、編輯、複製、移除
                if (isDraft) {
                  return (
                    <>
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={() => {
                          setActionSheetOpened(false);
                          handlePublishList(selectedActivityId);
                        }}
                      >
                        發佈
                      </Button>
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={() => {
                          setActionSheetOpened(false);
                          handleEditList(selectedActivityId);
                        }}
                      >
                        編輯
                      </Button>
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={() => {
                          setActionSheetOpened(false);
                          handleDuplicateList(selectedActivityId);
                        }}
                      >
                        複製
                      </Button>
                      <Button
                        variant="destructive"
                        className="w-full"
                        onClick={() => {
                          setActionSheetOpened(false);
                          handleDeleteList(selectedActivityId);
                        }}
                      >
                        移除
                      </Button>
                    </>
                  );
                }

                // 「已結束」inactive：進行交付、複製、移除
                if (isInactive) {
                  return (
                    <>
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={() => {
                          setActionSheetOpened(false);
                          if (groupId) {
                            navigate(
                              `/lite/deliver/${selectedActivityId}/result?groupId=${encodeURIComponent(groupId)}`
                            );
                          } else {
                            navigate(
                              `/lite/deliver/${selectedActivityId}/result`
                            );
                          }
                        }}
                      >
                        進行交付
                      </Button>
                      <Button
                        variant="default"
                        className="w-full"
                        onClick={() => {
                          setActionSheetOpened(false);
                          handleDuplicateList(selectedActivityId);
                        }}
                      >
                        複製
                      </Button>
                      <Button
                        variant="destructive"
                        className="w-full"
                        onClick={() => {
                          setActionSheetOpened(false);
                          handleDeleteList(selectedActivityId);
                        }}
                      >
                        移除
                      </Button>
                    </>
                  );
                }

                // 未知狀態（向後相容）：同 draft
                return (
                  <>
                    <Button
                      variant="default"
                      className="w-full"
                      onClick={() => {
                        setActionSheetOpened(false);
                        handlePublishList(selectedActivityId);
                      }}
                    >
                      發佈
                    </Button>
                    <Button
                      variant="default"
                      className="w-full"
                      onClick={() => {
                        setActionSheetOpened(false);
                        handleEditList(selectedActivityId);
                      }}
                    >
                      編輯
                    </Button>
                    <Button
                      variant="default"
                      className="w-full"
                      onClick={() => {
                        setActionSheetOpened(false);
                        handleDuplicateList(selectedActivityId);
                      }}
                    >
                      複製
                    </Button>
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={() => {
                        setActionSheetOpened(false);
                        handleDeleteList(selectedActivityId);
                      }}
                    >
                      移除
                    </Button>
                  </>
                );
              })()}
          </div>
          <DrawerFooter>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setActionSheetOpened(false)}
            >
              取消
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Drawer - 從菜單建立（上傳圖片辨識） */}
      <Drawer open={menuRecognizeOpen} onOpenChange={setMenuRecognizeOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>從菜單建立</DrawerTitle>
            <DrawerDescription>
              上傳 1～3 張菜單或飲料單圖片，AI
              會辨識品項與價格並產生活動草稿，您可編輯後再儲存。多張圖片會合併成一份。
            </DrawerDescription>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-4">
            <input
              type="file"
              accept="image/*"
              multiple
              className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded file:border-0 file:bg-primary file:px-4 file:py-2 file:text-primary-foreground"
              onChange={e => {
                const files = e.target.files ? Array.from(e.target.files) : [];
                setMenuRecognizeFiles(prev => [...prev, ...files].slice(0, 3));
                e.target.value = '';
              }}
            />
            {menuRecognizeFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  已選 {menuRecognizeFiles.length}/3 張圖片
                </p>
                <ul className="max-h-32 overflow-y-auto text-sm">
                  {menuRecognizeFiles.map((f, i) => (
                    <li
                      key={`${f.name}-${i}`}
                      className="flex items-center justify-between"
                    >
                      <span className="truncate">{f.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setMenuRecognizeFiles(prev =>
                            prev.filter((_, idx) => idx !== i)
                          )
                        }
                      >
                        移除
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {recognizeError && (
              <p className="text-sm text-destructive">{recognizeError}</p>
            )}
          </div>
          <DrawerFooter>
            <Button
              variant="outline"
              onClick={() => {
                setMenuRecognizeOpen(false);
                setMenuRecognizeFiles([]);
                setRecognizeError(null);
              }}
            >
              取消
            </Button>
            <Button
              onClick={handleRecognizeSubmit}
              disabled={isRecognizing || menuRecognizeFiles.length === 0}
            >
              {isRecognizing ? '辨識中…' : '辨識並預填'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Dialog - 發佈成功後的分享連結；與 Join 一致，僅分享到 LINE */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>分享活動連結</DialogTitle>
            <DialogDescription>
              點擊下方連結可開啟活動頁面，僅可分享至 LINE
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {shareDialogText && (
              <pre className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3 max-h-24 overflow-auto whitespace-pre-wrap break-words">
                {shareDialogText}
              </pre>
            )}
            <Button
              className="w-full"
              onClick={() => handleShareToLineFromDialog()}
            >
              分享到 LINE
            </Button>
          </div>
          <DialogFooter />
        </DialogContent>
      </Dialog>

      {/* Dialog - 刪除確認 */}
      <Dialog open={deleteDialogOpened} onOpenChange={setDeleteDialogOpened}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>確認刪除</DialogTitle>
            <DialogDescription>
              確定要刪除此活動嗎？此操作無法復原。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpened(false)}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (selectedActivityId) {
                  await handleConfirmDelete();
                }
              }}
            >
              刪除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
